import { lowerCase, startCase } from 'lodash/fp';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import InquiriesGatingSticker, {
  EnhancedGatingStickerType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiriesGatingSticker';
import { SectionDescription } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import ContextMenu from 'dashboard/components/ContextMenu';
import {
  INSIGHT_TYPE_TO_DISPLAY_NAME,
  RESPONSE_TYPE_TO_DISPLAY_NAME,
} from 'dashboard/components/InsightPanel/util';
import ProductUpsellTooltip from 'dashboard/components/ProductUpsellTooltip';
import useLearnMoreEventClick from 'dashboard/components/ProductUpsellTooltip/useLearnMoreEventClick';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { DOCUMENT_AI_BLOCK_MESSAGE } from 'dashboard/forms/FormVerificationDocumentTemplateConfig/FormVerificationDocumentTemplateConfig';
import {
  FieldWriteBackRequest,
  FieldWriteBackSourceType,
  InsightRequest,
  InsightType,
} from 'dashboard/models/document';
import { OrganizationPlanKey } from 'dashboard/models/organization-plan';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Label, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';

const TableContainer = styled.div`
  max-height: 512px;
  overflow-y: auto;
`;

const EmptyTable = ({ colSpan, children }: { colSpan: number; children: React.ReactNode }) => (
  <TableRow style={{ height: '80px' }}>
    <TableCell colSpan={colSpan}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {children}
      </div>
    </TableCell>
  </TableRow>
);

const LearnMoreTable = ({
  id,
  accessTier,
  colSpan,
}: {
  id: string;
  accessTier: Exclude<EditorAccessTier, EditorAccessTier.Any>;
  colSpan: number;
}) => {
  const { linkElementId, onLearnMoreClick } = useLearnMoreEventClick({ id });

  return (
    <EmptyTable colSpan={colSpan}>
      <Label weight='bold'>This feature is not available on your plan</Label>
      <Spacer height={0.5} />
      <a
        href='#'
        target='_blank'
        id={linkElementId}
        rel='noreferrer'
        onClick={(e) => {
          e.preventDefault();
          onLearnMoreClick({ accessTier });
        }}
        style={{ textDecoration: 'underline' }}
      >
        Learn more
      </a>
    </EmptyTable>
  );
};

const ActivableTableRow = styled(TableRow)`
  background-color: ${({ theme, active }) => active && theme.color.background.highlight};
`;

interface InsightsTableProps {
  insights: InsightRequest[];
  selected: number | null;
  onSelect: (index?: number | null) => void;
  onDelete: (index: number) => void;
  fieldWriteBacks: FieldWriteBackRequest[];
  insightLimit: number;
  aiLimitReached: boolean;
  editable: boolean;
}

const InsightsTable = ({
  insights,
  selected,
  onSelect,
  onDelete,
  fieldWriteBacks,
  insightLimit,
  aiLimitReached,
  editable,
}: InsightsTableProps) => {
  // TODO(Documents): *DO NOT COPY THIS LOGIC* to check the current plan of the Organization. This logic
  // will be replaced by explicit ProductFeatures that will control this functionality in the near future.
  const currentOrganizationPlan = useSelector(
    (state: RootState) => state.organization.currentOrganizationPlan,
  );
  const planAllowsAccessToEnhancedFeatures =
    currentOrganizationPlan?.attributes?.['key'] === OrganizationPlanKey.Managed ||
    currentOrganizationPlan?.attributes?.['key'] === OrganizationPlanKey.TrialSalesProspect ||
    currentOrganizationPlan?.attributes?.['key'] === OrganizationPlanKey.Partner ||
    currentOrganizationPlan?.attributes?.['key'] === OrganizationPlanKey.Internal;

  const [searchString, setSearchString] = useState('');

  const filteredInsights = insights
    .map((insight, index) => [insight, index] as const)
    .filter(
      ([i]) =>
        lowerCase(i.label).includes(lowerCase(searchString)) ||
        lowerCase(startCase(i.key)).includes(lowerCase(searchString)),
    );

  useEffect(() => {
    setSearchString('');
  }, [insights]);

  const handleDelete = (index: number) => {
    onDelete(index);
    if (selected === index) {
      onSelect(undefined);
    } else if (selected != null && selected > index) {
      onSelect(selected - 1);
    }
  };

  const insightLimitReached = insights.length >= insightLimit;

  return (
    <Card>
      <Card.Body>
        <Card.Title
          actions={
            <Tooltip
              side='top'
              disabled={
                !editable ||
                !planAllowsAccessToEnhancedFeatures ||
                (!insightLimitReached && !aiLimitReached)
              }
              trigger={
                <Button
                  id='insights-card__add-button'
                  variant='basic'
                  size='sm'
                  onClick={() => onSelect(null)}
                  disabled={
                    !editable ||
                    !planAllowsAccessToEnhancedFeatures ||
                    insightLimitReached ||
                    aiLimitReached
                  }
                >
                  Add insight
                </Button>
              }
            >
              {insightLimitReached && (
                <Paragraph>{`A maximum of ${insightLimit} insights can be configured for a document.`}</Paragraph>
              )}
              {aiLimitReached && <Paragraph>{DOCUMENT_AI_BLOCK_MESSAGE}</Paragraph>}
            </Tooltip>
          }
        >
          Insights
          {!planAllowsAccessToEnhancedFeatures && (
            <ProductUpsellTooltip
              accessTier={EditorAccessTier.InquiriesEnhanced}
              learnMoreId='verifications_document_classifications'
              trigger={
                <div>
                  <InquiriesGatingSticker stickerType={EnhancedGatingStickerType.Limited} />
                </div>
              }
            />
          )}
        </Card.Title>
        <SectionDescription>
          Insights are takeaways, summaries, or evaluations derived from documents via AI.
        </SectionDescription>
        <SearchInput
          title={null}
          placeholder='Search insights'
          value={searchString}
          handleInput={setSearchString}
          disabled={!editable || !planAllowsAccessToEnhancedFeatures}
        />
        <Spacer height={0.5} />
        <TableContainer>
          <Table>
            <TableHead sticky>
              <TableRow>
                <TableHeaderCell>Label & key</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell>Response type</TableHeaderCell>
                <TableHeaderCell>Save to document field</TableHeaderCell>
                <TableHeaderCell width='min' />
              </TableRow>
            </TableHead>
            <TableBody>
              {!planAllowsAccessToEnhancedFeatures && insights.length === 0 ? (
                <LearnMoreTable
                  id='verifications_document_insights'
                  accessTier={EditorAccessTier.InquiriesEnhanced}
                  colSpan={5}
                />
              ) : insights.length === 0 ? (
                <EmptyTable colSpan={5}>No insights configured</EmptyTable>
              ) : filteredInsights.length === 0 ? (
                <EmptyTable colSpan={5}>No insights found</EmptyTable>
              ) : (
                filteredInsights.map(([insight, index]) => {
                  // NB: this only gets the first document field that has this insight being written to
                  const fieldWriteBack = fieldWriteBacks.find((fieldWriteBack) =>
                    fieldWriteBack.options.some(
                      (option) =>
                        option['source-type'] === FieldWriteBackSourceType.Insight &&
                        option['source-key'] === insight.key,
                    ),
                  );

                  return (
                    <ActivableTableRow
                      key={index}
                      id={insight.key}
                      onClick={
                        editable && planAllowsAccessToEnhancedFeatures
                          ? () => onSelect(index)
                          : undefined
                      }
                      active={index === selected}
                    >
                      <TableCell>
                        {insight.label}
                        <Paragraph color='tertiary'>{insight.key}</Paragraph>
                      </TableCell>
                      <TableCell>{INSIGHT_TYPE_TO_DISPLAY_NAME[insight.type]}</TableCell>
                      <TableCell>
                        {insight.type === InsightType.MultiSelect ? (
                          RESPONSE_TYPE_TO_DISPLAY_NAME[insight['response-type']]
                        ) : (
                          <Paragraph weight='light'>N/A</Paragraph>
                        )}
                      </TableCell>
                      <TableCell>
                        {fieldWriteBack?.['field-name'] ?? (
                          <Paragraph weight='light'>None</Paragraph>
                        )}
                      </TableCell>
                      <TableCell>
                        <ContextMenu
                          variant='inverse'
                          disabled={!editable || !planAllowsAccessToEnhancedFeatures}
                          items={[
                            {
                              id: 'insights-table__context-menu-delete',
                              label: 'Delete',
                              onClick: () => handleDelete(index),
                              variant: 'danger',
                            },
                          ]}
                          renderItem={(item) => (
                            <ContextMenu.Item {...item}>{item.label}</ContextMenu.Item>
                          )}
                        />
                      </TableCell>
                    </ActivableTableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card.Body>
    </Card>
  );
};

export default InsightsTable;
